import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, FiSearch, FiRefreshCw, 
  FiDownload, FiTrash2, FiEye, FiShield 
} from 'react-icons/fi';
import { litigationAPI } from '../../services/api';
import { toast } from 'react-toastify';
import LitigationFormModal from './LitigationFormModal';

const PHASE_CONFIG = {
  amicable: { label: 'Phase Amiable', color: '#64748b', bg: '#f1f5f9' },
  formal_notice: { label: 'Mise en Demeure (30j)', color: '#b91c1c', bg: '#fee2e2' },
  bailiff_commandment: { label: 'Commandement Huissier', color: '#c2410c', bg: '#ffedd5' },
  court_referral: { label: 'Saisine Tribunal', color: '#7c3aed', bg: '#ede9fe' },
  court_hearing: { label: 'Audience Tribunal', color: '#2563eb', bg: '#dbeafe' },
  judgment_rendered: { label: 'Jugement / Référé Rendu', color: '#0284c7', bg: '#e0f2fe' },
  enforcement_eviction: { label: 'Expulsion / Force Publique', color: '#dc2626', bg: '#fee2e2' },
  settled_closed: { label: 'Accord / Clos', color: '#16a34a', bg: '#dcfce7' },
  abandoned: { label: 'Abandonné', color: '#94a3b8', bg: '#f8fafc' },
};

const DISPUTE_LABELS = {
  unpaid_rent: 'Loyers Impayés',
  property_damage: 'Dégradations',
  illegal_sublease: 'Sous-location Illégale',
  lease_violation: 'Violation Clauses Bail',
  refusal_to_vacate: 'Déguerpissement / Expulsion',
  deposit_dispute: 'Litige Caution',
  other: 'Autre Litige',
};

const LitigationsListPage = () => {
  const [litigations, setLitigations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [disputeFilter, setDisputeFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(null);

  const navigate = useNavigate();

  const fetchLitigations = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        litigationAPI.list({
          phase: phaseFilter !== 'all' ? phaseFilter : undefined,
          dispute_type: disputeFilter !== 'all' ? disputeFilter : undefined,
          search: searchTerm || undefined,
        }),
        litigationAPI.getStats(),
      ]);
      setLitigations(Array.isArray(listRes.data) ? listRes.data : []);
      setStats(statsRes.data || {});
    } catch (err) {
      toast.error('Erreur chargement des dossiers de contentieux');
    } finally {
      setLoading(false);
    }
  }, [phaseFilter, disputeFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLitigations();
    }, 200);
    return () => clearTimeout(timer);
  }, [fetchLitigations]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer définitivement ce dossier de contentieux ?')) return;
    try {
      await litigationAPI.delete(id);
      toast.success('Dossier de litige supprimé');
      fetchLitigations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = async (litigation, e) => {
    e.stopPropagation();
    setDownloadingPDF(litigation.id);
    try {
      const res = await litigationAPI.downloadFormalNoticePDF(litigation.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mise-En-Demeure-${litigation.case_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Mise en demeure (Art. 35 Loi 2016-037) téléchargée');
    } catch (e) {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10, color: '#0f172a' }}>
            <FiShield color="#dc2626" /> Contentieux & Litiges Locatifs (Loi Malienne)
          </h1>
          <p style={{ color: 'var(--text-secondary, #64748b)', margin: '6px 0 0', fontSize: 13 }}>
            Procédure d'impayés, mises en demeure 30j (Art. 35 Loi 2016-037), commandements d'huissier et référés expulsion.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={fetchLitigations} 
            disabled={loading}
            title="Actualiser la liste"
          >
            <FiRefreshCw className={loading ? 'spin' : ''} /> Actualiser
          </button>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={() => setIsModalOpen(true)}
            style={{ background: '#dc2626', borderColor: '#dc2626' }}
          >
            <FiPlus /> Ouvrir un Litige
          </button>
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Dossiers Actifs</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: '#0f172a' }}>{stats.active_litigations || 0}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 600, textTransform: 'uppercase' }}>Mises en Demeure (30j)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626', marginTop: 4 }}>{stats.formal_notices_count || 0}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase' }}>Affaires au Tribunal</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#7c3aed', marginTop: 4 }}>{stats.court_cases_count || 0}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>Montants Recouvrés</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginTop: 4 }}>
            {parseFloat(stats.total_recovered || 0).toLocaleString('fr-FR')} XOF
          </div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <div style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>Reste à Recouvrer</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginTop: 4 }}>
            {parseFloat(stats.total_outstanding_balance || 0).toLocaleString('fr-FR')} XOF
          </div>
        </div>
      </div>

      {/* ── Filters & Search ── */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: 20, borderRadius: 10 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: 36, fontSize: 13 }}
              placeholder="Rechercher par n° dossier, locataire, bien, bail ou huissier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Phase Filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Phase Légale :</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
            >
              <option value="all">Toutes les phases</option>
              <option value="amicable">Phase Amiable</option>
              <option value="formal_notice">Mise en Demeure (30j)</option>
              <option value="bailiff_commandment">Commandement Huissier</option>
              <option value="court_referral">Saisine Tribunal</option>
              <option value="court_hearing">Audience</option>
              <option value="judgment_rendered">Jugement Rendu</option>
              <option value="enforcement_eviction">Expulsion Force Publique</option>
              <option value="settled_closed">Accord / Clos</option>
            </select>
          </div>

          {/* Dispute Type Filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Motif :</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
              value={disputeFilter}
              onChange={(e) => setDisputeFilter(e.target.value)}
            >
              <option value="all">Tous les motifs</option>
              <option value="unpaid_rent">Loyers Impayés</option>
              <option value="property_damage">Dégradations</option>
              <option value="illegal_sublease">Sous-location Illégale</option>
              <option value="refusal_to_vacate">Déguerpissement</option>
              <option value="deposit_dispute">Litige Caution</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Table Container avec Défilement Horizontal Fluide ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Chargement des dossiers de litiges...</p>
        </div>
      ) : litigations.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center', borderRadius: 10 }}>
          <FiShield size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px', color: '#1e293b' }}>Aucun contentieux enregistré</h3>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 450, margin: '0 auto 20px', fontSize: 14 }}>
            {searchTerm || phaseFilter !== 'all' || disputeFilter !== 'all'
              ? 'Aucun litige ne correspond aux critères sélectionnés.'
              : 'Aucun contentieux ou impayé en cours pour votre agence.'}
          </p>
          <button className="btn btn-primary" onClick={() => setIsModalOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dc2626', borderColor: '#dc2626' }}>
            <FiPlus /> Ouvrir un Litige
          </button>
        </div>
      ) : (
        <div className="card" style={{ borderRadius: 10, overflow: 'hidden' }}>
          <div className="table-container">
            <table style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg, #f8fafc)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                  <th style={thStyle}>Dossier & Motif</th>
                  <th style={thStyle}>Locataire & Contact</th>
                  <th style={thStyle}>Bien Occupé & Bail</th>
                  <th style={thStyle}>Créance & Recouvrement</th>
                  <th style={thStyle}>Phase Procédurale</th>
                  <th style={thStyle}>Actes & Échéances</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {litigations.map((lit) => {
                  const phaseInfo = PHASE_CONFIG[lit.phase] || { label: lit.phase, color: '#334155', bg: '#f1f5f9' };
                  const disputeLabel = DISPUTE_LABELS[lit.dispute_type] || lit.dispute_type;
                  const remaining = parseFloat(lit.remaining_balance || 0);

                  return (
                    <tr
                      key={lit.id}
                      onClick={() => navigate(`/litigations/${lit.id}`)}
                      style={{ borderBottom: '1px solid var(--border, #e2e8f0)', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg, #f8fafc)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Dossier & Motif */}
                      <td style={tdStyle}>
                        <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626', fontSize: 13 }}>
                          {lit.case_number}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, marginTop: 3 }}>
                          {disputeLabel}
                        </div>
                      </td>

                      {/* Locataire */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                          {lit.tenant_first_name} {lit.tenant_last_name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
                          📞 {lit.tenant_phone || 'Non renseigné'}
                        </div>
                      </td>

                      {/* Bien & Bail */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {lit.property_title}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
                          {lit.unit_number ? `Unité ${lit.unit_number} · ` : ''}{lit.property_address}
                        </div>
                        {lit.contract_number && (
                          <div style={{ fontSize: 10, color: 'var(--primary, #2563eb)', fontFamily: 'monospace', marginTop: 2 }}>
                            Contrat : {lit.contract_number}
                          </div>
                        )}
                      </td>

                      {/* Créance & Recouvrement */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }}>
                          {parseFloat(lit.total_claimed_amount || 0).toLocaleString('fr-FR')} XOF
                        </div>
                        <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                          Recouvré : {parseFloat(lit.recovered_amount || 0).toLocaleString('fr-FR')} XOF
                        </div>
                        {remaining > 0 && (
                          <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 600, marginTop: 1 }}>
                            Solde restant : {remaining.toLocaleString('fr-FR')} XOF
                          </div>
                        )}
                      </td>

                      {/* Phase Procédurale */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: phaseInfo.bg,
                            color: phaseInfo.color,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {phaseInfo.label}
                        </span>
                        {lit.phase === 'formal_notice' && lit.formal_notice_deadline && (
                          <div style={{ fontSize: 10, color: '#b91c1c', marginTop: 3 }}>
                            Délai 30j : {new Date(lit.formal_notice_deadline).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </td>

                      {/* Actes & Échéances */}
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#334155', fontWeight: 600 }}>
                            📜 {lit.events_count || 0}
                          </span>
                          {lit.schedules_count > 0 && (
                            <span style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#166534', fontWeight: 600 }}>
                              📅 Échéancier
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            title="Voir le dossier"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/litigations/${lit.id}`);
                            }}
                            style={actionBtn('var(--primary, #2563eb)')}
                          >
                            <FiEye size={14} />
                          </button>
                          <button
                            type="button"
                            title="Générer Mise en Demeure PDF"
                            disabled={downloadingPDF === lit.id}
                            onClick={(e) => handleDownloadPDF(lit, e)}
                            style={actionBtn('#dc2626')}
                          >
                            <FiDownload size={14} />
                          </button>
                          <button
                            type="button"
                            title="Supprimer le dossier"
                            onClick={(e) => handleDelete(lit.id, e)}
                            style={actionBtn('#64748b')}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal création */}
      <LitigationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchLitigations}
      />
    </div>
  );
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary, #64748b)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap'
};
const tdStyle = { padding: '12px 16px', fontSize: 13, verticalAlign: 'middle' };
const actionBtn = (color) => ({
  width: 32, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer',
  background: color + '15', color: color, fontSize: 14,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background 0.2s', flexShrink: 0
});

export default LitigationsListPage;
